/**
 * Derives lease availability windows from published listing `form` (list-asset JSON).
 */
import dayjs from 'dayjs';

const DURATION_UNITS = new Set(['days', 'weeks', 'months', 'years']);

const UNIT_TO_DAYJS = {
  days: 'day',
  weeks: 'week',
  months: 'month',
  years: 'year',
};

function parseDay(iso) {
  if (iso == null || String(iso).trim() === '') return null;
  const d = dayjs(String(iso).slice(0, 10), 'YYYY-MM-DD');
  return d.isValid() ? d.startOf('day') : null;
}

export function normalizeDurationType(t) {
  const s = String(t || '').toLowerCase();
  return DURATION_UNITS.has(s) ? s : 'months';
}

export function durationTypeLabel(durationType) {
  const d = normalizeDurationType(durationType);
  if (d === 'days') return 'Days';
  if (d === 'weeks') return 'Weeks';
  if (d === 'months') return 'Months';
  return 'Years';
}

/**
 * @param {object|null|undefined} form
 * @returns {{
 *   durationType: string,
 *   availableFrom: import('dayjs').Dayjs,
 *   windowEndInclusive: import('dayjs').Dayjs,
 *   totalUnits: number,
 *   minTenure: number|null,
 *   maxTenure: number|null,
 *   flexibleTenure: boolean,
 *   listingTitle: string,
 *   bookedRanges: { start: import('dayjs').Dayjs, end: import('dayjs').Dayjs }[],
 * }}
 */
/**
 * Optional discrete slots: [{ from/to | start/end | startDate/endDate }, ...].
 * When non-empty, a date is selectable only if it falls inside the listing window AND inside at least one slot.
 */
function parseAvailabilitySlotRanges(form) {
  const raw = form?.availabilitySlots;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const ranges = [];
  for (const slot of raw) {
    if (!slot || typeof slot !== 'object') continue;
    const a = parseDay(slot.from ?? slot.start ?? slot.startDate ?? slot.availableFrom);
    const b = parseDay(slot.to ?? slot.end ?? slot.endDate ?? slot.availableTo);
    if (!a || !b) continue;
    const start = a.isBefore(b) ? a : b;
    const end = b.isAfter(a) ? b : a;
    ranges.push({ start, end });
  }
  return ranges.length ? ranges : null;
}

/**
 * @param {object|null|undefined} form Listing form JSON (list-asset shape).
 * @param {{ inventoryAvailability?: { bookedRanges?: { start: string, end: string }[] }, bookedRanges?: { start: string, end: string }[] }|undefined} options
 *        Optional server-derived occupancy (confirmed leases / reserved allocations).
 */
export function buildAvailabilityContext(form, options) {
  const durationType = normalizeDurationType(form?.durationType);
  const djUnit = UNIT_TO_DAYJS[durationType];

  const rawFrom = form?.availableFrom;
  let availableFrom = rawFrom ? dayjs(String(rawFrom).slice(0, 10), 'YYYY-MM-DD') : null;
  if (!availableFrom || !availableFrom.isValid()) {
    availableFrom = dayjs().startOf('day');
  } else {
    availableFrom = availableFrom.startOf('day');
  }

  const durRaw = form?.availableDuration ?? form?.availableFor;
  let totalUnits = Number(durRaw);
  if (!Number.isFinite(totalUnits) || totalUnits <= 0) {
    totalUnits =
      durationType === 'years' ? 2 : durationType === 'months' ? 24 : durationType === 'weeks' ? 52 : 365;
  }

  const windowEndExclusive = availableFrom.add(totalUnits, djUnit);
  const windowEndInclusive = windowEndExclusive.subtract(1, 'day').endOf('day');

  const minRaw = Number(form?.minTenure);
  const maxRaw = Number(form?.maxTenure);
  const minTenure = Number.isFinite(minRaw) && minRaw > 0 ? minRaw : null;
  const maxTenure = Number.isFinite(maxRaw) && maxRaw > 0 ? maxRaw : null;

  const slotRanges = parseAvailabilitySlotRanges(form || {});

  const rawBooked =
    options?.inventoryAvailability?.bookedRanges ??
    options?.bookedRanges ??
    null;
  const bookedRanges = [];
  if (Array.isArray(rawBooked)) {
    for (const br of rawBooked) {
      if (!br || typeof br !== 'object') continue;
      const s = br.start ?? br.from;
      const e = br.end ?? br.to;
      if (s == null || e == null) continue;
      const a = dayjs(String(s).slice(0, 10), 'YYYY-MM-DD');
      const b = dayjs(String(e).slice(0, 10), 'YYYY-MM-DD');
      if (!a.isValid() || !b.isValid()) continue;
      const start = a.isBefore(b) ? a.startOf('day') : b.startOf('day');
      const end = b.isAfter(a) ? b.startOf('day') : a.startOf('day');
      bookedRanges.push({ start, end });
    }
  }

  return {
    durationType,
    availableFrom,
    windowEndInclusive,
    totalUnits,
    minTenure,
    maxTenure,
    flexibleTenure: Boolean(form?.flexibleTenure),
    listingTitle: form?.listingTitle != null ? String(form.listingTitle).trim() : '',
    bookedRanges,
    slotRanges,
  };
}

/** True if this calendar day cannot be part of a lease per seller listing rules. */
export function isDateUnavailableForSeller(date, ctx) {
  if (!date?.isValid?.()) return true;
  const d = date.startOf('day');
  if (d.isBefore(ctx.availableFrom.startOf('day'))) return true;
  if (d.isAfter(ctx.windowEndInclusive.startOf('day'))) return true;
  if (ctx.slotRanges?.length) {
    const inSlot = ctx.slotRanges.some(({ start, end }) => !d.isBefore(start.startOf('day')) && !d.isAfter(end.startOf('day')));
    if (!inSlot) return true;
  }
  for (const br of ctx.bookedRanges || []) {
    if (!br?.start || !br?.end) continue;
    const bs = br.start.startOf('day');
    const be = br.end.startOf('day');
    if (!d.isBefore(bs, 'day') && !d.isAfter(be, 'day')) return true;
  }
  return false;
}

/**
 * Buyer requirement fields from `infrastructure_leasing_requirement.form_json`.
 * Aliases: minimumDuration / maximumDuration / requiredByDate.
 */
export function buildBuyerRequirementContext(form) {
  if (!form || typeof form !== 'object') {
    return {
      durationType: 'months',
      minDuration: null,
      maxDuration: null,
      requiredBy: null,
      hasConstraints: false,
    };
  }
  const durationType = normalizeDurationType(form.durationType);
  const minRaw = Number(form.minDuration ?? form.minimumDuration);
  const maxRaw = Number(form.maxDuration ?? form.maximumDuration);
  const minDuration = Number.isFinite(minRaw) && minRaw > 0 ? minRaw : null;
  const maxDuration = Number.isFinite(maxRaw) && maxRaw > 0 ? maxRaw : null;
  let requiredBy = parseDay(form.requiredBy ?? form.requiredByDate);
  const hasConstraints = Boolean(requiredBy || minDuration != null || maxDuration != null);
  return {
    durationType,
    minDuration,
    maxDuration,
    requiredBy,
    hasConstraints,
  };
}

/** Inclusive end date from lease start + whole units of listing duration type. */
export function computeInclusiveEndFromStartAndUnits(start, units, durationType) {
  if (!start?.isValid?.() || !Number.isFinite(units) || units <= 0) return null;
  const dj = UNIT_TO_DAYJS[normalizeDurationType(durationType)];
  return start.startOf('day').add(units, dj).subtract(1, 'day');
}

/** Approximate lease length in listing duration units (for syncing duration input with range). */
export function inferLeaseUnitsFromRange(start, end, durationType) {
  if (!start?.isValid?.() || !end?.isValid?.()) return null;
  const s = start.startOf('day');
  const e = end.startOf('day');
  if (e.isBefore(s)) return null;
  const spanDays = e.diff(s, 'day') + 1;
  const dj = UNIT_TO_DAYJS[normalizeDurationType(durationType)];
  const ref = dayjs('2024-01-01').startOf('day');
  const oneUnitDays = Math.max(1, ref.add(1, dj).diff(ref, 'day'));
  return Math.max(0.01, Math.round((spanDays / oneUnitDays) * 1000) / 1000);
}

/**
 * Cross-check seller listing window + tenure + buyer requirement (timeline + min/max duration).
 * @returns {{ ok: boolean, sellerOk: boolean, buyerOk: boolean, messages: string[], warnings: string[] }}
 */
export function validateSellerBuyerLeaseRange(start, end, sellerCtx, buyerCtx) {
  const messages = [];
  const warnings = [];
  const seller = validateLeaseRange(start, end, sellerCtx);
  if (!seller.ok) {
    messages.push(seller.message || 'Outside listing availability or tenure rules.');
    return { ok: false, sellerOk: false, buyerOk: false, messages, warnings };
  }
  if (!buyerCtx || !buyerCtx.hasConstraints) {
    if (buyerCtx && buyerCtx.durationType !== sellerCtx.durationType) {
      warnings.push(
        `Buyer duration type (${durationTypeLabel(buyerCtx.durationType)}) differs from your listing (${durationTypeLabel(sellerCtx.durationType)}).`
      );
    }
    return { ok: true, sellerOk: true, buyerOk: true, messages, warnings };
  }
  let buyerOk = true;
  const s = start.startOf('day');
  const e = end.startOf('day');
  const spanDays = e.diff(s, 'day') + 1;

  if (buyerCtx.requiredBy && s.isAfter(buyerCtx.requiredBy.startOf('day'))) {
    buyerOk = false;
    messages.push(
      `Lease must start on or before the buyer's required-by date (${buyerCtx.requiredBy.format('YYYY-MM-DD')}).`
    );
  }

  const bUnit = UNIT_TO_DAYJS[buyerCtx.durationType];
  if (buyerCtx.minDuration != null) {
    const minEnd = s.add(buyerCtx.minDuration, bUnit).subtract(1, 'day');
    const minDays = minEnd.diff(s, 'day') + 1;
    if (spanDays < minDays) {
      buyerOk = false;
      messages.push(
        `Lease length must be at least ${buyerCtx.minDuration} ${durationTypeLabel(buyerCtx.durationType).toLowerCase()} (buyer requirement).`
      );
    }
  }
  if (buyerCtx.maxDuration != null) {
    const maxEnd = s.add(buyerCtx.maxDuration, bUnit).subtract(1, 'day');
    const maxDays = maxEnd.diff(s, 'day') + 1;
    if (spanDays > maxDays) {
      buyerOk = false;
      messages.push(
        `Lease length must not exceed ${buyerCtx.maxDuration} ${durationTypeLabel(buyerCtx.durationType).toLowerCase()} (buyer requirement).`
      );
    }
  }

  if (buyerCtx.durationType !== sellerCtx.durationType) {
    warnings.push(
      `Buyer duration type (${durationTypeLabel(buyerCtx.durationType)}) differs from your listing (${durationTypeLabel(sellerCtx.durationType)}); constraints use each side's units.`
    );
  }

  return { ok: buyerOk, sellerOk: true, buyerOk, messages, warnings };
}

export const QUOTATION_VALIDATION_MESSAGES = {
  sellerAvailability: 'Selected date is outside seller availability',
  buyerTimeline: 'Does not meet buyer required timeline',
  buyerDuration: 'Duration not within allowed range',
};

/**
 * Flags for real-time UI (three headline checks). Buyer timeline/duration default OK when buyer has no constraints.
 */
export function getRequirementQuotationValidationFlags(start, end, sellerCtx, buyerCtx) {
  const seller = validateLeaseRange(start, end, sellerCtx);
  const sellerAvailabilityOk = seller.ok;

  let buyerTimelineOk = true;
  let buyerDurationRangeOk = true;
  const warningMessages = [];

  if (!start?.isValid?.() || !end?.isValid?.()) {
    warningMessages.push(QUOTATION_VALIDATION_MESSAGES.sellerAvailability);
    return {
      sellerAvailabilityOk: false,
      buyerTimelineOk: false,
      buyerDurationRangeOk: false,
      warningMessages,
      allOk: false,
    };
  }

  if (!sellerAvailabilityOk) {
    warningMessages.push(QUOTATION_VALIDATION_MESSAGES.sellerAvailability);
  }

  const s = start.startOf('day');
  const e = end.startOf('day');

  if (buyerCtx?.hasConstraints) {
    if (buyerCtx.requiredBy && s.isAfter(buyerCtx.requiredBy.startOf('day'))) {
      buyerTimelineOk = false;
      warningMessages.push(QUOTATION_VALIDATION_MESSAGES.buyerTimeline);
    }
    const spanDays = e.diff(s, 'day') + 1;
    const bUnit = UNIT_TO_DAYJS[buyerCtx.durationType];
    let durationFail = false;
    if (buyerCtx.minDuration != null) {
      const minEnd = s.add(buyerCtx.minDuration, bUnit).subtract(1, 'day');
      const minDays = minEnd.diff(s, 'day') + 1;
      if (spanDays < minDays) durationFail = true;
    }
    if (buyerCtx.maxDuration != null) {
      const maxEnd = s.add(buyerCtx.maxDuration, bUnit).subtract(1, 'day');
      const maxDays = maxEnd.diff(s, 'day') + 1;
      if (spanDays > maxDays) durationFail = true;
    }
    if (durationFail) {
      buyerDurationRangeOk = false;
      warningMessages.push(QUOTATION_VALIDATION_MESSAGES.buyerDuration);
    }
  }

  const allOk = sellerAvailabilityOk && buyerTimelineOk && buyerDurationRangeOk;
  return {
    sellerAvailabilityOk,
    buyerTimelineOk,
    buyerDurationRangeOk,
    warningMessages: [...new Set(warningMessages)],
    allOk,
  };
}

/** Minimum lease span in whole days (inclusive start/end) from listing tenure fields. */
export function minimumLeaseDays(start, minTenure, durationType) {
  if (minTenure == null) return 0;
  const djUnit = UNIT_TO_DAYJS[normalizeDurationType(durationType)];
  const endNeeded = start.add(minTenure, djUnit);
  return endNeeded.diff(start, 'day');
}

/** Maximum lease span in whole days (inclusive). */
export function maximumLeaseDays(start, maxTenure, durationType) {
  if (maxTenure == null) return Number.POSITIVE_INFINITY;
  const djUnit = UNIT_TO_DAYJS[normalizeDurationType(durationType)];
  const endMax = start.add(maxTenure, djUnit).subtract(1, 'day').endOf('day');
  return endMax.diff(start, 'day') + 1;
}

/**
 * @param {import('dayjs').Dayjs} start
 * @param {import('dayjs').Dayjs} end
 * @param {ReturnType<typeof buildAvailabilityContext>} ctx
 * @returns {{ ok: boolean, message?: string }}
 */
export function validateLeaseRange(start, end, ctx) {
  if (!start || !end || !start.isValid() || !end.isValid()) {
    return { ok: false, message: 'Please select a start and end date.' };
  }
  const s = start.startOf('day');
  const e = end.startOf('day');
  if (e.isBefore(s)) {
    return { ok: false, message: 'End date must be on or after start date.' };
  }
  if (s.isBefore(ctx.availableFrom.startOf('day'))) {
    return { ok: false, message: `Lease cannot start before Available from (${ctx.availableFrom.format('YYYY-MM-DD')}).` };
  }
  if (e.isAfter(ctx.windowEndInclusive.endOf('day'))) {
    return {
      ok: false,
      message: `Lease cannot extend past the listing availability window (through ${ctx.windowEndInclusive.format('YYYY-MM-DD')}).`,
    };
  }

  if (ctx.slotRanges?.length) {
    let cur = s.startOf('day');
    const last = e.startOf('day');
    while (!cur.isAfter(last)) {
      const day = cur;
      const inSlot = ctx.slotRanges.some(
        ({ start: ss, end: se }) =>
          !day.isBefore(ss.startOf('day')) && !day.isAfter(se.startOf('day'))
      );
      if (!inSlot) {
        return {
          ok: false,
          message: 'Selected dates must fall inside a published availability slot.',
        };
      }
      cur = cur.add(1, 'day');
    }
  }

  const spanDays = e.diff(s, 'day') + 1;

  if (ctx.minTenure != null) {
    const minDays = minimumLeaseDays(s, ctx.minTenure, ctx.durationType);
    if (spanDays < minDays) {
      return {
        ok: false,
        message: `Lease must be at least ${ctx.minTenure} ${durationTypeLabel(ctx.durationType).toLowerCase()} (~${minDays} days minimum).`,
      };
    }
  }

  if (ctx.maxTenure != null && !ctx.flexibleTenure) {
    const maxDays = maximumLeaseDays(s, ctx.maxTenure, ctx.durationType);
    if (Number.isFinite(maxDays) && spanDays > maxDays) {
      return {
        ok: false,
        message: `Lease cannot exceed ${ctx.maxTenure} ${durationTypeLabel(ctx.durationType).toLowerCase()} for this listing.`,
      };
    }
  }

  for (const br of ctx.bookedRanges || []) {
    if (!br?.start || !br?.end) continue;
    const bs = br.start.startOf('day');
    const be = br.end.startOf('day');
    if (!s.isAfter(be, 'day') && !e.isBefore(bs, 'day')) {
      return { ok: false, message: 'Selected dates overlap a booked period.' };
    }
  }

  return { ok: true };
}

export function formatLeaseTimelineSummary(start, end, ctx) {
  const unit = durationTypeLabel(ctx.durationType);
  return `Proposed lease: ${start.format('YYYY-MM-DD')} → ${end.format('YYYY-MM-DD')} (listing window: ${ctx.availableFrom.format('YYYY-MM-DD')}–${ctx.windowEndInclusive.format('YYYY-MM-DD')}; duration type: ${unit}; available for ${ctx.totalUnits} ${unit.toLowerCase()} total)`;
}

/** Timeline line persisted on quotation when dates + listing (+ optional buyer) are known. */
export function buildQuotationTimelineLine(start, end, listingForm, buyerForm) {
  if (!start?.isValid?.() || !end?.isValid?.() || !listingForm) return '';
  const sc = buildAvailabilityContext(listingForm);
  const bc = buildBuyerRequirementContext(buyerForm || {});
  let line = formatLeaseTimelineSummary(start, end, sc);
  const chk = validateSellerBuyerLeaseRange(start, end, sc, bc);
  if (chk.ok && bc.hasConstraints) {
    line += ' Matches buyer requirement timeline & duration.';
  }
  return line;
}

/** Try to recover range from a previously saved timeline line. */
export function parseLeaseRangeFromText(text) {
  if (text == null || String(text).trim() === '') return null;
  const s = String(text);
  const m = s.match(/(\d{4}-\d{2}-\d{2})\s*[–—-]\s*(\d{4}-\d{2}-\d{2})/);
  if (!m) return null;
  const a = dayjs(m[1], 'YYYY-MM-DD');
  const b = dayjs(m[2], 'YYYY-MM-DD');
  if (!a.isValid() || !b.isValid()) return null;
  return [a.startOf('day'), b.startOf('day')];
}
